/* Curved photographic surfaces for the orbit experiment. Uses its existing
   scroll playhead; no independent timer, scroll listener or animation loop.

   ONE SIZE FOR EVERY PHOTOGRAPH. Each used to be shown whole, in its own
   proportions, inside the same square: a square photograph filled it and a
   16:9 one was a strip across its middle — and, being shorter, was let grow
   wider than the square ones, so going from product to product the picture
   grew and shrank. Ahtisam, 17 September 2026, of the barber scissors sets:
   Razpro p71 and p55 (both square) are 'the size what I want ... every single
   product to be stretched to this size. If they are bigger, then they're
   going to be squeezed to this size.' So the sheet is always the square, at
   the size a square photograph always had, and each photograph is scaled up
   or down to COVER it: one wider than the square loses its sides, a taller
   one its top and bottom, evenly, and none is ever squashed. The same rule,
   and the same uFit, as the menu's subcategory photograph
   (suvorna-menu-sub.js). The DOM photographs this takes over from are whole
   in the ring and zoom to the same cut as they come into focus, so the
   hand-over does not change the picture (suvorna-nail-orbit.css). */
(function (global) {
  'use strict';
  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
  function smooth(a, b, v) { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); }

  // Same inside-of-a-drum geometry as the skincare hero. The axis lies at
  // z = radius, rather than on the photo, so rolling travels instead of flipping.
  function curvedPoint(x, y, radius, roll) {
    const angle = y / radius + roll;
    return { x, y: radius * Math.sin(angle), z: radius * (1 - Math.cos(angle)) };
  }
  function rollState(index, cursor) {
    const angle = (cursor - index) * 1.12;
    return { angle, opacity: 1 - smooth(.40, 1.28, Math.abs(angle)) };
  }

  // The side of the one square every photograph is shown on: the orbit's own
  // photo size, enlarged as the drum takes over (blend 0..1) and, on a desktop,
  // fitted at its central stop. Shared with the divider, which stands as tall.
  function fitSize(size, w, h, blend = 1) {
    const photoSize = size * (1 + (w < 768 ? .04 : w < 1024 ? .10 : .25) * blend);
    if (w < 1024) return photoSize;
    // Fit the enlarged, tilted square. Account for the bowed surface's
    // perspective so its edges clear the copy/header.
    const lean = .055 * blend;
    const distance = h / (2 * Math.tan(35 * Math.PI / 360));
    const extent = (Math.cos(lean) + Math.sin(lean)) / 2;
    const depth = 1.35 * (1 - Math.cos(.5 / 1.35));
    const roomX = w * .26 - 8;
    const roomY = Math.min(h * .49 - 110, h * .51 - 86);
    return Math.min(photoSize, roomX / (extent + roomX * depth / distance), roomY / (extent + roomY * depth / distance));
  }

  function create({ THREE, art, items, invalidate }) {
    if (!THREE || !art || !items.length) return null;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    } catch (_) { return null; }
    const canvas = renderer.domElement;
    canvas.className = 'sv-orbit__drum';
    canvas.setAttribute('aria-hidden', 'true');
    art.appendChild(canvas);
    renderer.setPixelRatio(Math.min(global.devicePixelRatio || 1, 1.75));
    renderer.setClearColor(0xffffff, 0);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, 1, .1, 10000);
    const geometry = new THREE.PlaneGeometry(1, 1, 1, 64);
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    let width = 0, height = 0, disposed = false, lost = false;
    const records = items.map(() => ({ loaded: false, failed: false, texture: null, mesh: null, aspect: 1 }));
    function restoreImages() {
      items.forEach(item => { item.querySelector('img').style.visibility = ''; });
      canvas.style.opacity = '0';
    }
    function contextLost(event) {
      event.preventDefault(); lost = true; restoreImages(); invalidate();
    }
    function contextRestored() { lost = false; invalidate(); }
    canvas.addEventListener('webglcontextlost', contextLost);
    canvas.addEventListener('webglcontextrestored', contextRestored);
    const vertexShader = `
      uniform float uSize;
      uniform float uRadius;
      uniform float uRoll;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        float x = position.x * uSize;
        float y = position.y * uSize;
        float angle = y / uRadius + uRoll;
        vec3 curved = vec3(x, uRadius * sin(angle), uRadius * (1.0 - cos(angle)));
        gl_Position = projectionMatrix * modelViewMatrix * vec4(curved, 1.0);
      }`;
    const fragmentShader = `
      uniform sampler2D uMap;
      uniform vec2 uFit;
      uniform float uOpacity;
      varying vec2 vUv;
      void main() {
        // uFit: how much of the photograph the square shows each way, 1 being all of it.
        vec4 photo = texture2D(uMap, (vUv - 0.5) * uFit + 0.5);
        // Subtle edge shade describes the curve without changing the central photo.
        float edge = abs(vUv.y - 0.5) * 2.0;
        photo.rgb *= 1.0 - 0.055 * edge * edge;
        gl_FragColor = vec4(photo.rgb, photo.a * uOpacity);
      }`;
    items.forEach((item, index) => {
      const image = item.querySelector('img');
      if (!image) return;
      // Shopify's image URL retains the crop/aspect and contains no proxy or video.
      const src = image.currentSrc || image.src;
      const record = records[index];
      const texture = loader.load(src, loaded => {
        if (disposed) { loaded.dispose(); return; }
        record.aspect = loaded.image.width / loaded.image.height;
        loaded.minFilter = THREE.LinearFilter;
        loaded.generateMipmaps = false;
        loaded.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
        // Shader samples the original sRGB photo directly, matching the DOM image.
        const material = new THREE.ShaderMaterial({
          uniforms: { uMap: { value: loaded }, uSize: { value: 1 }, uFit: { value: new THREE.Vector2(Math.min(1, 1 / record.aspect), Math.min(1, record.aspect)) }, uRadius: { value: 1 }, uRoll: { value: 0 }, uOpacity: { value: 0 } },
          vertexShader, fragmentShader, side: THREE.DoubleSide,
          transparent: true, depthWrite: false, depthTest: false
        });
        record.mesh = new THREE.Mesh(geometry, material);
        // Vertices are transformed in the shader, beyond the unit plane's CPU bounds.
        record.mesh.frustumCulled = false;
        record.mesh.visible = false;
        scene.add(record.mesh);
        record.loaded = true;
        invalidate();
      }, undefined, () => { record.loaded = false; record.failed = true; invalidate(); });
      record.texture = texture;
    });

    return {
      isFallback(cursor) {
        return disposed || lost || records.some((record, i) => record.failed && rollState(i, cursor).opacity > .001);
      },
      render(frame, cursor, w, h) {
        if (disposed || lost || !w || !h) return 0;
        if (w !== width || h !== height) {
          width = w; height = h;
          renderer.setSize(w, h, false);
          camera.aspect = w / h;
          camera.position.z = h / (2 * Math.tan(35 * Math.PI / 360));
          const focusX = w < 768 ? w * .5 : w * .30;
          const focusY = h * (w < 768 ? .30 : .49);
          // Put the vanishing point at the product, not behind the right-hand text.
          camera.setViewOffset(w, h, w / 2 - focusX, h / 2 - focusY, w, h);
          camera.updateProjectionMatrix();
        }
        // Keep DOM photos until every currently visible drum photo is available.
        // Failed/missing images cannot leave the focused product blank.
        const states = records.map((record, i) => rollState(i, cursor));
        const ready = records.every((record, i) => states[i].opacity <= .001 || record.loaded);
        const blend = ready ? smooth(.90, 1, frame.morph) : 0;
        canvas.style.opacity = String(blend);
        if (blend === 0) return 0;
        // One sum for all of them: every photograph is the same square.
        const fittedSize = fitSize(frame.size, w, h, blend);
        const lean = (w < 768 ? .03 : .055) * blend;
        records.forEach((record, i) => {
          if (!record.mesh) return;
          const state = states[i];
          record.mesh.visible = state.opacity > .001;
          const u = record.mesh.material.uniforms;
          u.uSize.value = fittedSize;
          u.uRadius.value = fittedSize * 1.35;
          // The skincare hero's restrained left lean, applied to the whole drum.
          record.mesh.rotation.z = lean;
          u.uRoll.value = state.angle;
          u.uOpacity.value = state.opacity;
          record.mesh.renderOrder = Math.round(state.opacity * 100);
        });
        try { renderer.render(scene, camera); }
        catch (_) { lost = true; restoreImages(); return 0; }
        return blend;
      },
      dispose() {
        disposed = true;
        restoreImages();
        canvas.removeEventListener('webglcontextlost', contextLost);
        canvas.removeEventListener('webglcontextrestored', contextRestored);
        records.forEach(record => {
          record.texture?.dispose();
          record.mesh?.material.dispose();
        });
        geometry.dispose(); renderer.dispose(); canvas.remove();
      }
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { curvedPoint, rollState, fitSize, create };
  else global.SuvornaOrbitDrum = { create, fitSize };
})(typeof window !== 'undefined' ? window : globalThis);
